import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { buildQuoteComparison } from '../../../../lib/buildQuoteComparison';
import { pickReportComparisonBomIds } from '../../../../lib/buildSupplyChainReportContext';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialDataTable } from '../../report/editorial';
import { SectionShell } from '../SectionShell';
import { resolveSupplyChainPlan } from '../../../../lib/reportRendering/supplyChainPlan';

function fmtUsd(value: number): string {
  if (!Number.isFinite(value)) return '–';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

/**
 * W2b editorial port — each ad-hoc per-item quote table (mono header +
 * an app-mono-utility price cell) becomes an EditorialDataTable;
 * the Total / Lead columns are `numeric` so they render in the editorial
 * body-font tabular face (the IBM-Plex-shaped app mono is gone). Inputs
 * unchanged: same pickReportComparisonBomIds(plan, cod, 3) selection,
 * same ≥2-row comparison filter, intro with citation walking, identical
 * hasData gate.
 */
export const QuoteComparisonsSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const plan = resolveSupplyChainPlan(report);
  const cod = report.projectIntentAtGen?.inServiceDate ?? null;
  const bomIds = plan ? pickReportComparisonBomIds(plan, cod, 3) : [];
  const comparisons = bomIds
    .map((id) => buildQuoteComparison(plan!, id, cod))
    .filter((c): c is NonNullable<typeof c> => c != null && c.rows.length >= 2);
  const intro = report.supplyChainAnalysis?.quoteComparisonInsights?.trim();
  const hasData = comparisons.length > 0 || !!intro;

  return (
    <SectionShell label={label} hasData={hasData} report={report} sectionId="quoteComparisons" required>
      {intro && (
        <p className="text-sm mb-4" style={{ color: roleColor(theme, 'body') }}>{walkCitationText(intro, report.references)}</p>
      )}
      <div className="space-y-6">
        {comparisons.map((comparison) => (
          <div key={comparison.bomItem.id} className="space-y-2">
            <h3 className="text-sm font-bold" style={{ color: roleColor(theme, 'ink') }}>{comparison.bomItem.name}</h3>
            {/* data-span-columns: defensive — lets each wide quote-comparison
                table span both flow columns if supportsColumnLayout is on.
                Carrier div holds the testid (EditorialDataTable has no testid prop). */}
            <div data-span-columns className="overflow-x-auto" data-testid={`quote-comparison-${comparison.bomItem.id}`}>
              <EditorialDataTable
                theme={theme}
                columns={[
                  { header: 'Supplier' },
                  { header: 'Total', numeric: true, width: 110 },
                  { header: 'Lead (mo)', numeric: true, width: 110 },
                  { header: 'Risk' },
                ]}
                rows={comparison.rows.map((row) => [
                  row.supplier.name,
                  fmtUsd(row.totalCostUsd),
                  row.quote.leadTimeMonths,
                  row.risk.overallTier,
                ])}
              />
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};
